import { useRef, useEffect, useState } from 'react'
import { MAP_STYLES } from '../GoogleMap/GoogleMapStyle'
import haversine from 'haversine-distance'

export interface PolygonCoord {
  lat: number
  lng: number
}

export interface VenuePin {
  lat: number
  lng: number
  name: string
}

export function computeCentroid(pins: { lat: number; lng: number }[]): {
  lat: number
  lng: number
} {
  const lat = pins.reduce((s, p) => s + p.lat, 0) / pins.length
  const lng = pins.reduce((s, p) => s + p.lng, 0) / pins.length
  return { lat, lng }
}

const HEART_PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" height="48px" viewBox="0 -960 960 960" width="48px"><path d="M480-80Q319-217 239.5-334.5T160-552q0-150 96.5-239T480-880q127 0 223.5 89T800-552q0 100-79.5 217.5T480-80Z" fill="#AF231C"/><path d="M480-388q54-50 84-80t47-50q16-20 22.5-37t6.5-37q0-36-26-62t-62-26q-21 0-40.5 8.5T480-648q-12-15-31-23.5t-41-8.5q-36 0-62 26t-26 62q0 21 6 37t22 36q17 20 46 50t86 81Z" fill="#FFFFFF"/></svg>`

const HEART_PIN_ICON_URL = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(HEART_PIN_SVG)

interface LocationMapProps {
  center?: { lat: number; lon: number }
  mode: 'radius' | 'polygon'
  radiusKm: number
  onPolygonChange?: (coords: PolygonCoord[] | null) => void
  existingPolygon?: PolygonCoord[]
  venuePins?: VenuePin[]
}

const setZoomForRadius = (radius: number) => {
  if (radius < 1.5) return 14
  if (radius < 2.5) return 13
  if (radius <= 4.5) return 12
  if (radius < 10.1) return 11
  return 10
}

const POLY_STYLE = {
  fillColor: '#AF231C',
  fillOpacity: 0.1,
  strokeColor: '#AF231C',
  strokeWeight: 2,
}

const LocationMapWithDrawing = ({
  center: _center,
  mode,
  radiusKm,
  onPolygonChange,
  existingPolygon,
  venuePins,
}: LocationMapProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const circleRef = useRef<google.maps.Circle | null>(null)
  const markerRef = useRef<google.maps.Marker | null>(null)
  const venuePinMarkersRef = useRef<google.maps.Marker[]>([])
  const polygonRef = useRef<google.maps.Polygon | null>(null)
  const previewPolylineRef = useRef<google.maps.Polyline | null>(null)
  const rubberBandRef = useRef<google.maps.Polyline | null>(null)
  const verticesRef = useRef<google.maps.LatLng[]>([])
  const vertexMarkersRef = useRef<google.maps.Marker[]>([])
  const drawingActiveRef = useRef(false)
  const drawButtonRef = useRef<HTMLButtonElement | null>(null)

  const center = _center ? { lat: _center.lat, lng: _center.lon } : null

  useEffect(() => {
    if (!ref.current) return
    const gMap = new window.google.maps.Map(ref.current, {
      zoom: center ? setZoomForRadius(radiusKm) : 10,
      center: center,
      styles: MAP_STYLES,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
    })
    setMap(gMap)
  }, [])

  useEffect(() => {
    if (!map) return

    markerRef.current?.setMap(null)
    markerRef.current = null

    if (mode !== 'radius') {
      circleRef.current?.setMap(null)
      return
    }

    const pinCenter = venuePins?.length ? computeCentroid(venuePins) : null
    const effectiveCenter = center ?? pinCenter

    if (!effectiveCenter) {
      circleRef.current?.setMap(null)
      return
    }

    const minVenueRadiusKm =
      pinCenter && venuePins?.length
        ? Math.max(...venuePins.map((p) => haversine(pinCenter, p) / 1000)) * 1.2
        : 0
    const effectiveRadiusKm = Math.max(radiusKm, minVenueRadiusKm)
    const effectiveRadiusM = effectiveRadiusKm * 1000

    map.setCenter(effectiveCenter)
    map.setZoom(setZoomForRadius(effectiveRadiusKm))

    if (!venuePins?.length) {
      markerRef.current = new window.google.maps.Marker({ position: effectiveCenter, map })
    }

    if (circleRef.current) {
      circleRef.current.setCenter(effectiveCenter)
      circleRef.current.setRadius(effectiveRadiusM)
      circleRef.current.setMap(map)
    } else {
      circleRef.current = new window.google.maps.Circle({
        center: effectiveCenter,
        radius: effectiveRadiusM,
        map,
        fillColor: '#AF231C',
        fillOpacity: 0.05,
        strokeColor: '#AF231C',
        strokeWeight: 2,
      })
    }
  }, [map, _center?.lat, _center?.lon, mode, radiusKm, venuePins])

  useEffect(() => {
    if (!map) return
    venuePinMarkersRef.current.forEach((m) => m.setMap(null))
    venuePinMarkersRef.current = []

    if (!venuePins?.length) return

    const icon: google.maps.Icon = {
      url: HEART_PIN_ICON_URL,
      scaledSize: new google.maps.Size(40, 40),
      anchor: new google.maps.Point(20, 38),
    }

    venuePins.forEach((pin) => {
      const marker = new window.google.maps.Marker({
        position: { lat: pin.lat, lng: pin.lng },
        map,
        title: pin.name,
        icon,
        zIndex: 200,
      })
      venuePinMarkersRef.current.push(marker)
    })

    if (!_center) {
      const bounds = new google.maps.LatLngBounds()
      venuePins.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }))
      map.fitBounds(bounds, 80)
      google.maps.event.addListenerOnce(map, 'idle', () => {
        if ((map.getZoom() ?? 0) > 14) map.setZoom(14)
      })
    }
  }, [map, venuePins])

  useEffect(() => {
    if (!map) return

    if (mode !== 'polygon') {
      polygonRef.current?.setMap(null)
      polygonRef.current = null
      previewPolylineRef.current?.setMap(null)
      previewPolylineRef.current = null
      rubberBandRef.current?.setMap(null)
      rubberBandRef.current = null
      onPolygonChange?.(null)
      return
    }

    circleRef.current?.setMap(null)
    markerRef.current?.setMap(null)
    markerRef.current = null

    const emitCoords = (polygon: google.maps.Polygon) => {
      const coords = polygon
        .getPath()
        .getArray()
        .map((ll) => ({ lat: ll.lat(), lng: ll.lng() }))
      onPolygonChange?.(coords)
    }

    const attachPathListeners = (polygon: google.maps.Polygon) => {
      const path = polygon.getPath()
      google.maps.event.addListener(path, 'set_at', () => emitCoords(polygon))
      google.maps.event.addListener(path, 'insert_at', () => emitCoords(polygon))
      google.maps.event.addListener(path, 'remove_at', () => emitCoords(polygon))
    }

    const restartBtn = document.createElement('button')
    restartBtn.style.cssText = [
      'margin:10px',
      'padding:6px 14px',
      'background:white',
      'border:2px solid #AF231C',
      'color:#AF231C',
      'cursor:pointer',
      'font-size:13px',
      'font-family:Montserrat, Ariel, sans-serif',
      'border-radius:4px',
      'box-shadow:0 2px 4px rgba(0,0,0,0.2)',
    ].join(';')
    drawButtonRef.current = restartBtn

    const clearTempDrawing = () => {
      previewPolylineRef.current?.setMap(null)
      previewPolylineRef.current = null
      rubberBandRef.current?.setMap(null)
      rubberBandRef.current = null
      vertexMarkersRef.current.forEach((m) => m.setMap(null))
      vertexMarkersRef.current = []
      verticesRef.current = []
    }

    const setDrawingActive = (active: boolean) => {
      drawingActiveRef.current = active
      map.setOptions({ draggableCursor: active ? 'crosshair' : '', disableDoubleClickZoom: active })
      if (drawButtonRef.current) {
        drawButtonRef.current.textContent = active ? 'Restart' : 'Draw polygon'
      }
    }

    const finalizePolygon = (coords: google.maps.LatLng[]) => {
      clearTempDrawing()
      polygonRef.current?.setMap(null)

      const poly = new google.maps.Polygon({
        paths: coords,
        ...POLY_STYLE,
        editable: true,
        draggable: true,
        map,
      })
      polygonRef.current = poly
      emitCoords(poly)
      attachPathListeners(poly)
      setDrawingActive(false)
    }

    const updatePreviewPolyline = () => {
      const pts = verticesRef.current
      if (previewPolylineRef.current) {
        previewPolylineRef.current.setPath(pts)
      } else if (pts.length >= 2) {
        previewPolylineRef.current = new google.maps.Polyline({
          path: pts,
          strokeColor: '#AF231C',
          strokeWeight: 2,
          strokeOpacity: 0.7,
          clickable: false,
          map,
        })
      }
    }

    const updateFirstMarkerStyle = () => {
      const first = vertexMarkersRef.current[0]
      if (!first) return
      const canClose = verticesRef.current.length >= 3
      first.setIcon({
        path: google.maps.SymbolPath.CIRCLE,
        scale: canClose ? 10 : 5,
        fillColor: canClose ? '#fff' : '#AF231C',
        fillOpacity: 1,
        strokeColor: '#AF231C',
        strokeWeight: canClose ? 2.5 : 1.5,
      })
    }

    restartBtn.addEventListener('click', () => {
      clearTempDrawing()
      polygonRef.current?.setMap(null)
      polygonRef.current = null
      onPolygonChange?.(null)
      setDrawingActive(true)
    })

    map.controls[google.maps.ControlPosition.TOP_LEFT].push(restartBtn)

    const clickListener = google.maps.event.addListener(
      map,
      'click',
      (e: google.maps.MapMouseEvent) => {
        if (!drawingActiveRef.current || !e.latLng) return

        verticesRef.current.push(e.latLng)

        const isFirst = vertexMarkersRef.current.length === 0
        const dot = new google.maps.Marker({
          position: e.latLng,
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor: '#AF231C',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 1.5,
          },
          clickable: isFirst,
          zIndex: isFirst ? 99 : 1,
        })

        if (isFirst) {
          dot.addListener('click', () => {
            if (verticesRef.current.length >= 3) {
              finalizePolygon(verticesRef.current)
            }
          })
        }

        vertexMarkersRef.current.push(dot)
        updatePreviewPolyline()
        updateFirstMarkerStyle()

        rubberBandRef.current?.setMap(null)
        rubberBandRef.current = null
      }
    )

    const mouseMoveListener = google.maps.event.addListener(
      map,
      'mousemove',
      (e: google.maps.MapMouseEvent) => {
        if (!drawingActiveRef.current || !e.latLng || verticesRef.current.length < 1) return
        const last = verticesRef.current[verticesRef.current.length - 1]
        if (rubberBandRef.current) {
          rubberBandRef.current.setPath([last, e.latLng])
        } else {
          rubberBandRef.current = new google.maps.Polyline({
            path: [last, e.latLng],
            strokeColor: '#AF231C',
            strokeWeight: 1.5,
            strokeOpacity: 0.4,
            clickable: false,
            map,
          })
        }
      }
    )

    if (existingPolygon?.length) {
      const poly = new google.maps.Polygon({
        paths: existingPolygon,
        ...POLY_STYLE,
        editable: true,
        draggable: true,
        map,
      })
      polygonRef.current = poly
      attachPathListeners(poly)
      setDrawingActive(false)
      const bounds = new google.maps.LatLngBounds()
      existingPolygon.forEach((c) => bounds.extend(c))
      map.fitBounds(bounds, 40)
    } else {
      setDrawingActive(true)
    }

    return () => {
      google.maps.event.removeListener(clickListener)
      google.maps.event.removeListener(mouseMoveListener)
      clearTempDrawing()
      const controls = map.controls[google.maps.ControlPosition.TOP_LEFT]
      for (let i = 0; i < controls.getLength(); i++) {
        if (controls.getAt(i) === restartBtn) {
          controls.removeAt(i)
          break
        }
      }
      drawingActiveRef.current = false
      map.setOptions({ draggableCursor: '', disableDoubleClickZoom: false })
      drawButtonRef.current = null
    }
  }, [map, mode])

  return (
    <>
      <div
        ref={ref}
        style={{ height: '300px', width: '100%', borderRadius: '4px', overflow: 'hidden' }}
      />
    </>
  )
}

export default LocationMapWithDrawing
